import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useBlogApi } from '../hooks/useBlog';
import DOMPurify from 'dompurify';
import { Calendar, User, ArrowLeft, Tag, Share2, Linkedin, Twitter } from 'lucide-react';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getPostBySlug } = useBlogApi();

  useEffect(() => {
    loadPost();
    window.scrollTo(0, 0);
  }, [slug]);

  const loadPost = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPostBySlug(slug);
      setPost(data);
    } catch (error) {
      if (error.response?.status === 404) {
        setError('Article not found');
      } else {
        setError('Failed to load article');
      }
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = post ? `Check out: ${post.title}` : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
        <p className="font-body text-[#6B6860]">Loading article...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9F6F0]">
        <nav className="bg-[#F9F6F0] border-b border-[#D4D1C9]/30">
          <div className="container mx-auto px-6 md:px-12 lg:px-24">
            <div className="flex items-center justify-between h-16 md:h-20">
              <Link to="/" className="font-heading text-xl md:text-2xl font-semibold text-[#2C4A3E] tracking-tight">
                Fairway<span className="text-[#B89A5A]"> Partners</span>
              </Link>
            </div>
          </div>
        </nav>
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-24 text-center">
          <h1 className="font-heading text-3xl text-[#1A1A18] mb-4">{error}</h1>
          <p className="font-body text-[#6B6860] mb-8">The article you're looking for doesn't exist or has been removed.</p>
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 font-body text-[#2C4A3E] hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Navigation */}
      <nav className="bg-[#F9F6F0] border-b border-[#D4D1C9]/30">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="font-heading text-xl md:text-2xl font-semibold text-[#2C4A3E] tracking-tight">
              Fairway<span className="text-[#B89A5A]"> Partners</span>
            </Link>
            <Link to="/blog" className="font-body text-sm text-[#6B6860] hover:text-[#2C4A3E]">
              ← Back to Blog
            </Link>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <header className="py-12 md:py-20 border-b border-[#D4D1C9]">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-body text-sm font-medium text-[#B89A5A] uppercase tracking-wider">
                {post.category}
              </span>
            </div>
            <h1 
              data-testid="blog-post-title"
              className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#1A1A18] leading-tight"
            >
              {post.title}
            </h1>
            <p className="font-body text-lg text-[#6B6860] mt-6">
              {post.excerpt}
            </p>
            <div className="flex flex-wrap items-center gap-6 mt-8 pt-6 border-t border-[#D4D1C9]">
              <div className="flex items-center gap-2 font-body text-sm text-[#6B6860]">
                <User className="w-4 h-4" />
                {post.author}
              </div>
              <div className="flex items-center gap-2 font-body text-sm text-[#6B6860]">
                <Calendar className="w-4 h-4" />
                {new Date(post.published_at || post.created_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Featured Image */}
      {post.featured_image && (
        <div className="border-b border-[#D4D1C9]">
          <div className="container mx-auto px-6 md:px-12 lg:px-24">
            <div className="max-w-4xl mx-auto -mt-1">
              <img 
                src={post.featured_image} 
                alt={post.title}
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      )}

      {/* Article Content */}
      <article className="py-12 md:py-20">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto">
            <div 
              className="prose prose-lg max-w-none blog-content"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-[#D4D1C9]">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="w-4 h-4 text-[#6B6860]" />
                  {post.tags.map(tag => (
                    <span 
                      key={tag}
                      className="font-body text-sm text-[#6B6860] bg-[#E8E4D9] px-3 py-1"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Share */}
            <div className="mt-8 pt-8 border-t border-[#D4D1C9]">
              <div className="flex items-center gap-4">
                <span className="font-body text-sm text-[#6B6860]">Share this article:</span>
                <a 
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-[#E8E4D9] hover:bg-[#2C4A3E] hover:text-white transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-[#E8E4D9] hover:bg-[#2C4A3E] hover:text-white transition-colors"
                >
                  <Twitter className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 bg-[#2C4A3E] p-8 md:p-12">
              <h3 className="font-heading text-2xl font-medium text-white mb-4">
                Need Strategic Finance Support?
              </h3>
              <p className="font-body text-white/70 mb-6">
                Get in touch with our team of qualified professionals to discuss how we can help your business grow.
              </p>
              <Link 
                to="/#contact"
                className="inline-flex items-center gap-2 bg-[#B89A5A] text-white font-body px-6 py-3 hover:bg-[#B89A5A]/90 transition-colors"
              >
                Book a Consultation
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-[#1A1A18] py-12">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="font-heading text-xl font-semibold text-white tracking-tight">
              Fairway<span className="text-[#B89A5A]"> Partners</span>
            </div>
            <div className="flex gap-8">
              <Link to="/" className="font-body text-sm text-white/60 hover:text-white">Home</Link>
              <Link to="/blog" className="font-body text-sm text-white/60 hover:text-white">Blog</Link>
              <Link to="/#contact" className="font-body text-sm text-white/60 hover:text-white">Contact</Link>
            </div>
            <div className="font-body text-sm text-white/40">
              © {new Date().getFullYear()} Fairway Partners Limited
            </div>
          </div>
        </div>
      </footer>

      {/* Blog Content Styles */}
      <style>{`
        .blog-content {
          font-family: 'DM Sans', sans-serif;
          color: #1A1A18;
        }
        .blog-content h1,
        .blog-content h2,
        .blog-content h3,
        .blog-content h4 {
          font-family: 'Cormorant Garamond', serif;
          color: #1A1A18;
          font-weight: 500;
          margin-top: 2em;
          margin-bottom: 0.5em;
        }
        .blog-content h1 { font-size: 2em; }
        .blog-content h2 { font-size: 1.5em; }
        .blog-content h3 { font-size: 1.25em; }
        .blog-content p {
          margin-bottom: 1.5em;
          line-height: 1.8;
          color: #6B6860;
        }
        .blog-content a {
          color: #2C4A3E;
          text-decoration: underline;
        }
        .blog-content a:hover {
          color: #B89A5A;
        }
        .blog-content ul,
        .blog-content ol {
          margin-bottom: 1.5em;
          padding-left: 1.5em;
          color: #6B6860;
        }
        .blog-content li {
          margin-bottom: 0.5em;
        }
        .blog-content blockquote {
          border-left: 3px solid #B89A5A;
          padding-left: 1.5em;
          margin: 2em 0;
          font-style: italic;
          color: #6B6860;
        }
        .blog-content img {
          max-width: 100%;
          height: auto;
          margin: 2em 0;
        }
        .blog-content pre,
        .blog-content code {
          background: #E8E4D9;
          padding: 0.2em 0.4em;
          font-size: 0.9em;
        }
        .blog-content pre {
          padding: 1em;
          overflow-x: auto;
        }
      `}</style>
    </div>
  );
};

export default BlogPost;
